import * as React from "react"

// ── Valid HTML/SVG attribute set ─────────────────────────────────────
// Comprehensive set of valid DOM attributes. Derived from React's internal
// DOM property list (same source @emotion/is-prop-valid uses). This avoids
// an external dependency and compresses well with gzip.

const VALID_HTML_PROPS = new Set([
  // HTML global attributes
  "abbr",
  "accept",
  "acceptCharset",
  "accessKey",
  "action",
  "allow",
  "allowFullScreen",
  "allowTransparency",
  "alt",
  "as",
  "async",
  "autoCapitalize",
  "autoComplete",
  "autoCorrect",
  "autoFocus",
  "autoPlay",
  "autoSave",
  "capture",
  "cellPadding",
  "cellSpacing",
  "challenge",
  "charSet",
  "checked",
  "cite",
  "classID",
  "className",
  "colSpan",
  "cols",
  "content",
  "contentEditable",
  "contextMenu",
  "controls",
  "controlsList",
  "coords",
  "crossOrigin",
  "dangerouslySetInnerHTML",
  "data",
  "dateTime",
  "decoding",
  "default",
  "defaultChecked",
  "defaultValue",
  "defer",
  "dir",
  "disabled",
  "disablePictureInPicture",
  "disableRemotePlayback",
  "download",
  "draggable",
  "encType",
  "enterKeyHint",
  "fetchPriority",
  "form",
  "formAction",
  "formEncType",
  "formMethod",
  "formNoValidate",
  "formTarget",
  "frameBorder",
  "headers",
  "height",
  "hidden",
  "high",
  "href",
  "hrefLang",
  "htmlFor",
  "httpEquiv",
  "id",
  "imageSizes",
  "imageSrcSet",
  "inert",
  "inputMode",
  "integrity",
  "is",
  "itemID",
  "itemProp",
  "itemRef",
  "itemScope",
  "itemType",
  "keyParams",
  "keyType",
  "kind",
  "label",
  "lang",
  "list",
  "loading",
  "loop",
  "low",
  "manifest",
  "marginHeight",
  "marginWidth",
  "max",
  "maxLength",
  "media",
  "mediaGroup",
  "method",
  "min",
  "minLength",
  "multiple",
  "muted",
  "name",
  "noModule",
  "noValidate",
  "nonce",
  "open",
  "optimum",
  "pattern",
  "placeholder",
  "playsInline",
  "popover",
  "popoverTarget",
  "popoverTargetAction",
  "poster",
  "preload",
  "profile",
  "radioGroup",
  "readOnly",
  "referrerPolicy",
  "rel",
  "required",
  "results",
  "reversed",
  "role",
  "rows",
  "rowSpan",
  "sandbox",
  "scope",
  "scoped",
  "scrolling",
  "seamless",
  "security",
  "selected",
  "shape",
  "size",
  "sizes",
  "slot",
  "span",
  "spellCheck",
  "src",
  "srcDoc",
  "srcLang",
  "srcSet",
  "start",
  "step",
  "style",
  "summary",
  "tabIndex",
  "target",
  "title",
  "translate",
  "type",
  "unselectable",
  "useMap",
  "value",
  "width",
  "wmode",
  "wrap",
  // Event handlers
  "onAbort",
  "onAnimationEnd",
  "onAnimationIteration",
  "onAnimationStart",
  "onAuxClick",
  "onBeforeInput",
  "onBlur",
  "onChange",
  "onClick",
  "onCompositionEnd",
  "onCompositionStart",
  "onCompositionUpdate",
  "onContextMenu",
  "onCopy",
  "onCut",
  "onDoubleClick",
  "onDrag",
  "onDragEnd",
  "onDragEnter",
  "onDragExit",
  "onDragLeave",
  "onDragOver",
  "onDragStart",
  "onDrop",
  "onError",
  "onFocus",
  "onFocusIn",
  "onFocusOut",
  "onGotPointerCapture",
  "onInput",
  "onInvalid",
  "onKeyDown",
  "onKeyPress",
  "onKeyUp",
  "onLoad",
  "onLoadStart",
  "onLostPointerCapture",
  "onMouseDown",
  "onMouseEnter",
  "onMouseLeave",
  "onMouseMove",
  "onMouseOut",
  "onMouseOver",
  "onMouseUp",
  "onPaste",
  "onPause",
  "onPlay",
  "onPlaying",
  "onPointerCancel",
  "onPointerDown",
  "onPointerEnter",
  "onPointerLeave",
  "onPointerMove",
  "onPointerOut",
  "onPointerOver",
  "onPointerUp",
  "onProgress",
  "onRateChange",
  "onReset",
  "onResize",
  "onScroll",
  "onScrollEnd",
  "onSeeked",
  "onSeeking",
  "onSelect",
  "onStalled",
  "onSubmit",
  "onSuspend",
  "onTimeUpdate",
  "onToggle",
  "onTouchCancel",
  "onTouchEnd",
  "onTouchMove",
  "onTouchStart",
  "onTransitionEnd",
  "onVolumeChange",
  "onWaiting",
  "onWheel",
  // SVG attributes
  "accentHeight",
  "accumulate",
  "additive",
  "alignmentBaseline",
  "allowReorder",
  "alphabetic",
  "amplitude",
  "arabicForm",
  "ascent",
  "attributeName",
  "attributeType",
  "autoReverse",
  "azimuth",
  "baseFrequency",
  "baseProfile",
  "baselineShift",
  "bbox",
  "begin",
  "bias",
  "by",
  "calcMode",
  "capHeight",
  "clip",
  "clipPath",
  "clipPathUnits",
  "clipRule",
  "color",
  "colorInterpolation",
  "colorInterpolationFilters",
  "colorProfile",
  "colorRendering",
  "contentScriptType",
  "contentStyleType",
  "cursor",
  "cx",
  "cy",
  "d",
  "decelerate",
  "descent",
  "diffuseConstant",
  "direction",
  "display",
  "divisor",
  "dominantBaseline",
  "dur",
  "dx",
  "dy",
  "edgeMode",
  "elevation",
  "enableBackground",
  "end",
  "exponent",
  "externalResourcesRequired",
  "fill",
  "fillOpacity",
  "fillRule",
  "filter",
  "filterRes",
  "filterUnits",
  "flood",
  "floodColor",
  "floodOpacity",
  "focusable",
  "font",
  "fontFamily",
  "fontSize",
  "fontSizeAdjust",
  "fontStretch",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "format",
  "fr",
  "from",
  "fx",
  "fy",
  "g1",
  "g2",
  "glyphName",
  "glyphOrientationHorizontal",
  "glyphOrientationVertical",
  "glyphRef",
  "gradientTransform",
  "gradientUnits",
  "hanging",
  "horizAdvX",
  "horizOriginX",
  "ideographic",
  "imageRendering",
  "in",
  "in2",
  "intercept",
  "k",
  "k1",
  "k2",
  "k3",
  "k4",
  "kernelMatrix",
  "kernelUnitLength",
  "kerning",
  "keyPoints",
  "keySplines",
  "keyTimes",
  "lengthAdjust",
  "letterSpacing",
  "lightingColor",
  "limitingConeAngle",
  "local",
  "markerEnd",
  "markerHeight",
  "markerMid",
  "markerStart",
  "markerUnits",
  "markerWidth",
  "mask",
  "maskContentUnits",
  "maskUnits",
  "mathematical",
  "mode",
  "numOctaves",
  "offset",
  "opacity",
  "operator",
  "order",
  "orient",
  "orientation",
  "origin",
  "overflow",
  "overlinePosition",
  "overlineThickness",
  "paintOrder",
  "panose1",
  "pathLength",
  "patternContentUnits",
  "patternTransform",
  "patternUnits",
  "pointerEvents",
  "points",
  "pointsAtX",
  "pointsAtY",
  "pointsAtZ",
  "preserveAlpha",
  "preserveAspectRatio",
  "primitiveUnits",
  "r",
  "radius",
  "refX",
  "refY",
  "renderingIntent",
  "repeatCount",
  "repeatDur",
  "requiredExtensions",
  "requiredFeatures",
  "restart",
  "result",
  "rotate",
  "rx",
  "ry",
  "scale",
  "seed",
  "shapeRendering",
  "slope",
  "spacing",
  "specularConstant",
  "specularExponent",
  "speed",
  "spreadMethod",
  "startOffset",
  "stdDeviation",
  "stemh",
  "stemv",
  "stitchTiles",
  "stopColor",
  "stopOpacity",
  "strikethroughPosition",
  "strikethroughThickness",
  "string",
  "stroke",
  "strokeDasharray",
  "strokeDashoffset",
  "strokeLinecap",
  "strokeLinejoin",
  "strokeMiterlimit",
  "strokeOpacity",
  "strokeWidth",
  "surfaceScale",
  "suppressContentEditableWarning",
  "suppressHydrationWarning",
  "systemLanguage",
  "tableValues",
  "targetX",
  "targetY",
  "textAnchor",
  "textDecoration",
  "textLength",
  "textRendering",
  "to",
  "transform",
  "transformOrigin",
  "u1",
  "u2",
  "underlinePosition",
  "underlineThickness",
  "unicode",
  "unicodeBidi",
  "unicodeRange",
  "unitsPerEm",
  "vAlphabetic",
  "vHanging",
  "vIdeographic",
  "vMathematical",
  "values",
  "vectorEffect",
  "version",
  "vertAdvY",
  "vertOriginX",
  "vertOriginY",
  "viewBox",
  "viewTarget",
  "visibility",
  "widths",
  "wordSpacing",
  "writingMode",
  "x",
  "x1",
  "x2",
  "xChannelSelector",
  "xHeight",
  "xlinkActuate",
  "xlinkArcrole",
  "xlinkHref",
  "xlinkRole",
  "xlinkShow",
  "xlinkTitle",
  "xlinkType",
  "xmlBase",
  "xmlLang",
  "xmlSpace",
  "xmlns",
  "xmlnsXlink",
  "y",
  "y1",
  "y2",
  "yChannelSelector",
  "z",
  "zoomAndPan",
])

function isValidDOMProp(prop: string): boolean {
  if (prop === "children" || prop === "ref" || prop === "key") return true
  if (prop.startsWith("data-") || prop.startsWith("aria-")) return true
  return VALID_HTML_PROPS.has(prop)
}

// ── Types ────────────────────────────────────────────────────────────

interface DigitsCSSMeta {
  className: string
  extends?: string | React.ComponentType
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type VarsMap = Record<string, (props: any) => unknown>

interface StyledOptions {
  cls: string
  vars?: VarsMap
  /** Maps $-prefixed camelCase prop names to CSS custom property names.
   *  Used by function-call syntax (e.g. styled.div(`var(--text-color)`)).
   *  e.g. { "$textColor": "--text-color", "$spacing": "--spacing" } */
  cssVarMap?: Record<string, string>
}

function isObjectLike(value: unknown): value is object {
  return (typeof value === "object" && value !== null) || typeof value === "function"
}

function isDigitsCssComponent(component: unknown): component is StyledComponent {
  return isObjectLike(component) && "__digits_css" in component
}

// ── CSS variable prop type inference ─────────────────────────────────
// These types extract var(--name) patterns from a CSS string literal type
// and produce an object type with $-prefixed optional string props.

type ExtractVarName<S extends string> = S extends `${infer Name})${string}`
  ? ExtractVarNameFromComma<Name>
  : S extends `${infer Name} ${string}`
    ? Name
    : never

type ExtractVarNameFromComma<S extends string> = S extends `${infer Name},${string}` ? Name : S

type ExtractCSSVarNames<S extends string> = S extends `${string}var(--${infer Rest}`
  ? ExtractVarName<Rest> | ExtractCSSVarNames<Rest>
  : never

type CamelCase<S extends string> = S extends `${infer A}-${infer B}${infer C}`
  ? `${A}${Uppercase<B>}${CamelCase<C>}`
  : S

/** Extracts CSS variable names from a CSS string and maps them to $-prefixed optional string props. */
type CSSVarProps<S extends string> = {
  [K in ExtractCSSVarNames<S> as `$${CamelCase<K>}`]?: string
}

// Marker type for compile-time metadata (matches Linaria's WYWEvalMeta pattern).
// _sc satisfies styled-components' StyledComponentBrand, making StyledComponent
// accepted as an Interpolation in styled-components templates.
type EvalMeta = {
  __digits_css: DigitsCSSMeta
  toString: () => string
  readonly _sc: symbol
}

// Polymorphic `as` prop: when `as="a"` is used, accepts that element's HTML attributes (href, etc.).
// The polymorphic overload (`as` required) is tried first; the default (`as` optional) is last, so
// ComponentPropsWithoutRef extraction keeps `as` optional. Safe for tsgo — this type only appears
// in return position of HtmlStyledTag/ComponentStyledTag, so it doesn't affect template inference.

type ResolveProps<T> = [T] extends [React.FunctionComponent<infer P>] ? P : T

type StyledComponent<T = object> = EvalMeta & {
  // Polymorphic: when `as` specifies an intrinsic element, also accept that element's attributes
  <C extends keyof React.JSX.IntrinsicElements>(
    props: ResolveProps<T> & { as: C } & Omit<
        React.JSX.IntrinsicElements[C],
        keyof ResolveProps<T> | "as"
      >
  ): React.ReactElement | null
  // Default: original component props with optional `as`
  (props: ResolveProps<T> & { as?: React.ElementType }): React.ReactElement | null
  displayName?: string
}

// CSS properties for template interpolations (simple recursive type, matches Linaria's CSSProperties)
type CSSProperties = {
  [key: string]: string | number | CSSProperties
}

// Static values allowed in tagged template interpolations
type StaticPlaceholder = string | number | CSSProperties | EvalMeta

// Matches @linaria/react's HtmlStyledTag exactly, extended with function-call syntax.
type HtmlStyledTag<TName extends keyof React.JSX.IntrinsicElements> = {
  // Tagged template syntax: styled.div`color: red;`
  <TAdditionalProps = Record<never, unknown>>(
    strings: TemplateStringsArray,
    ...exprs: Array<
      | StaticPlaceholder
      | ((
          props: React.JSX.IntrinsicElements[TName] & Omit<TAdditionalProps, never>
        ) => string | number)
    >
  ): StyledComponent<React.JSX.IntrinsicElements[TName] & TAdditionalProps>
  // Function-call syntax: styled.div(`color: var(--text-color);`)
  // CSS variable names are auto-inferred as $-prefixed optional string props.
  <S extends string>(
    css: S
  ): StyledComponent<
    React.JSX.IntrinsicElements[TName] & CSSVarProps<S> & { as?: React.ElementType }
  >
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attrs(obj: Record<string, any>): HtmlStyledTag<TName>
}

type ComponentStyledTag<TProps> = {
  // Tagged template syntax
  <OwnProps = Record<never, unknown>>(
    strings: TemplateStringsArray,
    ...exprs: Array<
      StaticPlaceholder | ((props: TProps & Omit<OwnProps, never>) => string | number)
    >
  ): StyledComponent<OwnProps & TProps>
  // Function-call syntax
  <S extends string>(css: S): StyledComponent<TProps & CSSVarProps<S>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attrs(obj: Record<string, any>): ComponentStyledTag<TProps>
}

type StyledJSXIntrinsics = {
  readonly [P in keyof React.JSX.IntrinsicElements]: HtmlStyledTag<P>
}

type StyledProxy = StyledJSXIntrinsics & {
  // styled(Component) — returns a tag for tagged template or function-call usage
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <C extends React.ComponentType<any>>(
    component: C
  ): ComponentStyledTag<React.ComponentPropsWithRef<C>>
  // styled(Component, `css`) — function-call syntax with component wrapping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <C extends React.ComponentType<any>, S extends string>(
    component: C,
    css: S
  ): StyledComponent<React.ComponentPropsWithRef<C> & CSSVarProps<S>>
}

// ── Runtime implementation ───────────────────────────────────────────

function createStyledComponent(
  tag: string | React.ComponentType,
  options: StyledOptions | null,
  mockTag?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultAttrs?: Record<string, any>
): StyledComponent {
  const cls = options?.cls ?? (mockTag ? `mock-${mockTag}` : "mock-styled")
  const vars = options?.vars
  const cssVarMap = options?.cssVarMap
  const attrsClassName = defaultAttrs?.className as string | undefined

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = React.forwardRef<unknown, any>((props, ref) => {
    // Merge default attrs (from .attrs()) under actual props so explicit props win.
    // className from attrs is handled separately via attrsClassName to allow additive merging.
    const { className: _, ...attrsRest } = defaultAttrs ?? {}
    const mergedProps = { ...attrsRest, ...props }
    const { as: asProp, className: extraClassName, style: extraStyle, ...rest } = mergedProps

    const forwardsAsToWrappedComponent = asProp && isDigitsCssComponent(tag)
    const resolvedTag = forwardsAsToWrappedComponent ? tag : (asProp ?? tag)
    const resolvedIsDom = typeof resolvedTag === "string"

    // Compute CSS custom property values from vars functions (tagged template prop functions)
    let varStyle: Record<string, string> | undefined
    if (vars) {
      varStyle = {}
      for (const [varName, fn] of Object.entries(vars)) {
        const val = fn(rest)
        if (val !== undefined && val !== null) {
          varStyle[varName] = String(val)
        }
      }
    }

    // Map $-prefixed CSS variable props to inline style custom properties (function-call syntax).
    // Skip empty strings so that CSS fallback values (e.g. var(--delay, 250ms)) work correctly —
    // a set-but-empty custom property is "defined" in CSS and would suppress the fallback.
    if (cssVarMap) {
      for (const [propName, cssVar] of Object.entries(cssVarMap)) {
        const val = rest[propName]
        if (val !== undefined && val !== null && val !== "") {
          if (!varStyle) varStyle = {}
          varStyle[cssVar] = String(val)
        }
      }
    }

    const mergedStyle = varStyle || extraStyle ? { ...varStyle, ...extraStyle } : undefined

    const mergedClassName = [cls, attrsClassName, extraClassName].filter(Boolean).join(" ")

    // Filter props for DOM elements
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let filteredProps: any
    if (resolvedIsDom) {
      filteredProps = {} as Record<string, unknown>
      for (const key of Object.keys(rest)) {
        if (key.startsWith("$")) continue
        if (!isValidDOMProp(key)) continue
        filteredProps[key] = rest[key]
      }
    } else {
      // For component tags (not DOM elements), forward all props including $-prefixed ones —
      // EXCEPT $-prefixed props consumed by cssVarMap (those are handled above).
      // This matches styled-components' behavior: $ transient props are only filtered at
      // the DOM boundary. Component children are responsible for their own $ filtering.
      // Strip undefined values to match styled-components behavior — forwarding
      // role={undefined} (for example) would override a library's internal role="menu".
      filteredProps = {} as Record<string, unknown>
      for (const key of Object.keys(rest)) {
        if (rest[key] === undefined) continue
        if (cssVarMap && key in cssVarMap) continue
        filteredProps[key] = rest[key]
      }
    }

    return React.createElement(resolvedTag, {
      ...filteredProps,
      ...(forwardsAsToWrappedComponent ? { as: asProp } : null),
      ref,
      className: mergedClassName,
      style: mergedStyle,
    })
  })

  const displayName = cls.includes("_") ? cls.split("_")[0] : cls
  Component.displayName = displayName

  const styledComponent = Component as unknown as StyledComponent
  // eslint-disable-next-line no-underscore-dangle
  styledComponent.__digits_css = { className: cls, extends: tag }
  styledComponent.toString = () => `.${cls}`
  // Styled-components interop: when a digits-css component is interpolated as a
  // selector in an SC template, SC's flatten() checks isStyledComponent() which
  // requires `styledComponentId`. When an SC template wraps a digits-css component
  // via styled(Component), SC also reads `target` (the underlying element) and
  // `foldedComponentIds`. Setting these lets both paths work without warnings.
  const scInterop = styledComponent as unknown as {
    styledComponentId: string
    target: string | React.ComponentType
    foldedComponentIds: string
  }
  scInterop.styledComponentId = cls
  scInterop.target = tag
  scInterop.foldedComponentIds = ""
  // _sc satisfies styled-components' StyledComponentBrand type constraint.
  // eslint-disable-next-line no-underscore-dangle
  ;(styledComponent as unknown as { _sc: symbol })._sc = Symbol(cls)

  return styledComponent
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeStyledTag(tag: string | React.ComponentType, defaultAttrs?: Record<string, any>): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function styledTag(...args: any[]): StyledComponent {
    const first = args[0]
    // Production path: plugin-transformed options object (both tagged template and function-call)
    if (first && typeof first === "object" && "cls" in first) {
      return createStyledComponent(tag, first as StyledOptions, undefined, defaultAttrs)
    }
    // Test path: tagged template literal (no plugin)
    if (first && typeof first === "object" && "raw" in first) {
      const mockTag =
        typeof tag === "string"
          ? tag
          : (tag as React.ComponentType & { displayName?: string }).displayName ||
            tag.name ||
            "Component"
      return createStyledComponent(tag, null, mockTag, defaultAttrs)
    }
    // Test path: function-call syntax with CSS string (no plugin)
    if (typeof first === "string") {
      const mockTag =
        typeof tag === "string"
          ? tag
          : (tag as React.ComponentType & { displayName?: string }).displayName ||
            tag.name ||
            "Component"
      return createStyledComponent(tag, null, mockTag, defaultAttrs)
    }
    return createStyledComponent(tag, null, undefined, defaultAttrs)
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styledTag.attrs = (attrsObj: Record<string, any>) =>
    makeStyledTag(tag, { ...defaultAttrs, ...attrsObj })
  return styledTag
}

const styled = new Proxy((() => {}) as unknown as StyledProxy, {
  get(_target, prop: string) {
    return makeStyledTag(prop)
  },
  apply(_target, _thisArg, args: unknown[]) {
    const component = args[0] as React.ComponentType
    const styledTag = makeStyledTag(component)
    // styled(Component, `css`) — function-call syntax, invoke tag immediately
    if (args.length > 1) {
      return styledTag(args[1])
    }
    return styledTag
  },
})

export default styled
export type { StyledComponent, StyledProxy, DigitsCSSMeta, CSSVarProps }

/**
 * A tagged template literal that interpolates values into a CSS string.
 * Use instead of styled-components' `css` when composing CSS fragments
 * for styledRuntime/digits-css templates.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function css<_P = object>(strings: TemplateStringsArray, ...values: unknown[]): string {
  return strings.reduce<string>((acc, str, i) => acc + str + (values[i] ?? ""), "")
}

/**
 * Build-time keyframes animation.
 *
 * Production path (digits-css plugin): the plugin replaces `keyframes\`...\``
 * with `keyframes({ name: "generatedName" })`, and this function simply returns
 * the pre-assigned animation name.
 *
 * Test/dev path (no plugin): falls back to a tagged template that generates a
 * stable mock name from the CSS content for use in tests.
 */
export function keyframes(
  arg: { name: string } | TemplateStringsArray,
  ...values: unknown[]
): string {
  // Production path: plugin has transformed to keyframes({ name: "..." })
  if (arg && !Array.isArray(arg) && typeof (arg as { name: string }).name === "string") {
    return (arg as { name: string }).name
  }
  // Test/dev path: tagged template literal — generate a mock name from content
  const strings = arg as TemplateStringsArray
  const body = strings.reduce<string>((acc, str, i) => acc + str + (values[i] ?? ""), "")
  // Simple djb2 hash for a stable, short name
  let h = 5381
  for (let i = 0; i < body.length; i++) {
    h = ((h << 5) + h + body.charCodeAt(i)) >>> 0
  }
  return `mock-kf-${h.toString(16).slice(0, 7)}`
}
